import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { AccessRequestItem } from '../../../hooks/useUserPermissions';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, ContainerV2 } from '../../../stories/atoms/containerV2/ContainerV2';
import { Button } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { errors } from '../../../translations';
import { accessRequestTranslations } from '../../../translations/accessRequest';
import { convertCamelCaseToTitleCase, formatDateWithTimestamp } from '../../../utils';
import { StatusTableitem } from '../../accessApproval/statusTableItem/StatusTableItem';

enum RequestColumnType {
  AccessRequestId = 'accessRequestId',
  Role = 'role',
  Status = 'status',
  RequestedTimestamp = 'requestedTimestamp',
}

export type RequestStatusViewProps = {
  requests: AccessRequestItem[];
  onRequestNewRoleClick: () => void;
  isLoading?: boolean;
  profileName?: string;
};

export const RequestsStatusView: FC<RequestStatusViewProps> = ({
  requests,
  isLoading,
  profileName,
  onRequestNewRoleClick,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { accessRequestId, manageAccessModal, status, requestedTimestamp, hi, requestAssignmentsStatus } =
    accessRequestTranslations;

  const headerItems: HeaderRowItem<RequestColumnType>[] = [
    {
      id: RequestColumnType.AccessRequestId,
      content: <Typography>{t(accessRequestId.key, accessRequestId.defaultValue)}</Typography>,
    },
    {
      id: RequestColumnType.Role,
      content: <Typography>{t(manageAccessModal.role.key, manageAccessModal.role.defaultValue)}</Typography>,
    },
    {
      id: RequestColumnType.Status,
      content: <Typography>{t(status.key, status.defaultValue)}</Typography>,
    },
    {
      id: RequestColumnType.RequestedTimestamp,
      content: <Typography>{t(requestedTimestamp.key, requestedTimestamp.defaultValue)}</Typography>,
    },
  ];

  return (
    <ContainerV2
      data-testid="requests-status-view"
      padding={[12]}
      backgroundColor={mode === 'dark' ? 'backgroundDark' : 'baseGray'}
      height="100%"
    >
      <ColumnFlexContainer>
        <Typography type="h4" gutterBottom>
          {t(hi.key, hi.defaultValue)}
          {profileName ? ` ${convertCamelCaseToTitleCase(profileName)}` : ''}
        </Typography>
        <Typography type="body3">
          {t(requestAssignmentsStatus.text1.key, requestAssignmentsStatus.text1.defaultValue)}
        </Typography>
        <Typography type="body3">
          {t(requestAssignmentsStatus.text2.key, requestAssignmentsStatus.text2.defaultValue)}
        </Typography>
      </ColumnFlexContainer>
      <ColumnFlexContainer flex={1} padding={[8, 0]} height="80%" overflow="auto">
        <Table
          items={requests}
          isLoading={isLoading}
          headerItems={headerItems}
          stickyHeader
          disableHover
          noBorder
          getRowItems={(requestItem: AccessRequestItem) => {
            return [
              {
                id: RequestColumnType.AccessRequestId,
                content: <Typography>{requestItem.id}</Typography>,
              },
              {
                id: RequestColumnType.Role,
                content: (
                  <Typography>
                    {requestItem.requestedAssignment.title
                      ? convertCamelCaseToTitleCase(requestItem.requestedAssignment.title)
                      : '-'}
                  </Typography>
                ),
              },
              {
                id: RequestColumnType.Status,
                content: <StatusTableitem accessRequestItem={requestItem} />,
              },
              {
                id: RequestColumnType.RequestedTimestamp,
                content: (
                  <Typography>
                    {requestItem.requestTimestamp ? formatDateWithTimestamp(requestItem.requestTimestamp) : '-'}
                  </Typography>
                ),
              },
            ];
          }}
        />
      </ColumnFlexContainer>
      <Button
        label={t(errors.storeNotAssigned.button.key, errors.storeNotAssigned.button.defaultValue)}
        onClick={onRequestNewRoleClick}
      />
    </ContainerV2>
  );
};
